'use client';

import { Divider } from '../../../components/divider/Divider';
import {
  type ComponentProps,
  type PolymorphicComponent,
  type PolymorphicComponentProps,
} from '../../../types/component';
import { tv } from '../../../utils/tv';
import { useFormFieldSlotContext } from './FormFieldSlotContext';

const formFieldSlotClasses = {
  root: 'zds-form-field-slot',
} as const;

const formFieldSlotStyles = tv({
  slots: {
    root: [
      formFieldSlotClasses.root,
      // Constrain SVG height only — width scales with aspect ratio to support non-square SVGs (e.g. flags)
      'flex h-5 w-auto shrink-0 items-center text-md z--form-field-icon-color [&_svg]:h-5 [&_svg]:w-auto',
    ],
    divider: 'h-5 min-h-0 self-center', // override Divider's h-auto min-h-full self-stretch
  },
  variants: {
    isDisabled: {
      true: {
        root: 'disabled-foreground',
        divider: 'bg-[var(--disabled-foreground)]',
      },
    },
  },
});

interface FormFieldSlotSlotProps {
  divider?: ComponentProps<object, 'span'>;
}

interface FormFieldSlotBaseProps {
  /** Show a divider on the inner side of the slot (closest to the primary element). */
  showDivider?: boolean;
  /** Props for internal sub-elements. */
  slotProps?: FormFieldSlotSlotProps;
}

type FormFieldSlotProps<RootComponent extends React.ElementType = 'span'> =
  PolymorphicComponentProps<FormFieldSlotBaseProps, RootComponent>;

type FormFieldSlotComponent = PolymorphicComponent<
  FormFieldSlotBaseProps,
  'span'
>;

const FormFieldSlot: FormFieldSlotComponent = (props) => {
  const {
    component: Root = 'span',
    showDivider,
    slotProps,
    className,
    children,
    ref,
    ...rest
  } = props;
  const { position, isDisabled } = useFormFieldSlotContext();
  const styles = formFieldSlotStyles({ isDisabled });

  const root = (
    <Root {...rest} ref={ref} className={styles.root({ className })}>
      {children}
    </Root>
  );

  if (!showDivider) {
    return root;
  }

  const divider = (
    <Divider
      component="span"
      orientation="vertical"
      isDecorative
      {...slotProps?.divider}
      className={styles.divider({ className: slotProps?.divider?.className })}
    />
  );

  return (
    <>
      {position === 'end' && divider}
      {root}
      {position === 'start' && divider}
    </>
  );
};

FormFieldSlot.displayName = 'FormFieldSlot';

export {
  FormFieldSlot,
  type FormFieldSlotProps,
  type FormFieldSlotBaseProps,
  type FormFieldSlotSlotProps,
  formFieldSlotClasses,
  formFieldSlotStyles,
};
